// Helpers for React's server-side render
import { unescapeHtml } from "./stringHelpers";

const highlightStyle = "background-color: yellow; color: black;";

/**
 * Compare the two lines of HTML with some additional transformations to account for rendering quirks.
 */
const htmlLinesAreSame = (line1: string, line2: string) => {
  const [t1, t2] = [line1, line2]
    // Firefox transforms all colour codes into rgba? - so will always differ in client and server
    .map(line => line.replace(/background-color:\s*(#\w*;?|rgba?\(\d+,\s*\d+,\s*\d+\);)/g, ""))
    // We don't care about spacing between style attributes
    .map(line => line.replace(/:\s?/g, ":"))
    .map(line => line.replace(/;\s?/g, ";"))
    // Ignore trailing ; in style="" attribute
    .map(line => line.split("").reverse().join("").replace("\";", "\"").split("").reverse().join(""));
  return t1 == t2;
}

/**
 * Only load prettydiff library when needed - it's 1MB
 * @param onload
 */
const loadPrettyDiff = (onload: () => void) => {
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = "https://travellocal-build.s3-eu-west-1.amazonaws.com/prettydiff-browser.js";
  script.async = false;
  script.onload = onload;
  script.onerror = e => console.error(e);
  document.body.appendChild(script);
};

/**
 * Compare two sets of HTML and output the diff to the console.
 * @param serverHtml HTML from the server
 * @param clientHtml HTML from the client
 */
const diffHtml = (serverHtml: string, clientHtml: string) => {
  interface PrettyDiffOutput {
    diff: (["=" | "-" | "+", string] | ["r", string, string])[];
  }

  const prettydiff = window["prettydiff"];
  prettydiff.options = {
    ...prettydiff.options,
    language: "html",
    lexer: "markup",
    diff_format: "json",
    mode: "diff",
    attribute_sort: true,
    source: serverHtml,
    diff: clientHtml,
  };

  const { diff } = JSON.parse(prettydiff()) as PrettyDiffOutput;
  const hasDiffs = diff.filter(([type]) => type != "=").length > 0;
  if (!hasDiffs) {
    return;
  }

  console.groupCollapsed("%cSSR check: Found differences between server and client HTML:", highlightStyle);

  const lineBuffer: string[] = [];
  for (let lineNumber = 0; lineNumber < diff.length; lineNumber++) {
    const lineDiff = diff[lineNumber];
    switch (lineDiff[0]) {
      case "r":
        const line1 = unescapeHtml(lineDiff[1].trim());
        const line2 = unescapeHtml(lineDiff[2].trim());

        if (htmlLinesAreSame(line1, line2)) {
          break;
        }

        console.group(`L${lineNumber}`);
        console.debug("S %c%s", "color:yellow;", line1);
        console.debug("C %c%s", "color:orange;", line2);
        console.groupEnd();
        break;
      case "+":
      case "-":
        lineBuffer.push(unescapeHtml(lineDiff[1]));

        if (lineNumber + 1 >= diff.length || diff[lineNumber + 1][0] !== lineDiff[0]) {
          console.group(`L${lineNumber - lineBuffer.length}-${lineNumber}`);

          const colour = lineDiff[0] === "+" ? "color:green" : "color:red";
          const lines = lineBuffer.map(l => `${lineDiff[0]} ${l}`);
          console.debug("%c%s", colour, lines.join("\n"));

          console.groupEnd();

          lineBuffer.splice(0, lineBuffer.length);
        }
        break;
    }
  }

  console.groupEnd();
}

/**
 * Compare the HTML returned by the server render with the HTML from the initial client render.
 * Differences are logged to the console. See #1968 for context.
 */
export const checkSsrHtml = () => {
  if (!("MutationObserver" in window)) {
    console.warn("%cSSR check failed - browser must support MutationObserver https://caniuse.com/#feat=mutationobserver", highlightStyle);
  }

  performance.mark("ssrcheck-start");

  // Replace console.error to listen for React's warnings
  const err = console.error;
  let hydrationWarningReceived = false;
  console.error = (...args: string[]) => {
    const regexes = [
      /Warning.*Server.*Client.*/,
      /Warning: Did not expect.*/,
      /Warning: Expected.*HTML.*to contain.*/,
    ];

    if (regexes.some(regex => args[0].match(regex))) {
      hydrationWarningReceived = true;
      console.debug("%cSSR check: hydration error detected:", highlightStyle);
    }
    err(...args);
  };

  // Collect current HTML, wait for first change to DOM, and diff.
  const reactHostNode = document.getElementById("host");
  const serverHtml = reactHostNode.innerHTML;
  let observer: MutationObserver;
  observer = new MutationObserver(() => {
    if (hydrationWarningReceived) {
      const clientHtml = document.getElementById("host").innerHTML;
      loadPrettyDiff(() => {
        diffHtml(serverHtml, clientHtml);

        performance.mark("ssrcheck-end");
        performance.measure("ssrcheck", "ssrcheck-start", "ssrcheck-end");
        const durationMs = performance.getEntriesByName("ssrcheck")[0].duration;
        console.debug("%cSSR check: took %sms", highlightStyle, durationMs);
      });
    }
    else {
      console.debug("%cSSR check: no problems detected!", highlightStyle);
    }

    observer.disconnect();
    console.error = err;
  });

  observer.observe(reactHostNode, { childList: true, subtree: true, attributeOldValue: true, attributes: true });
};
